import React, { useEffect, useRef, useState } from 'react';
import type { Brand, BrandingPatch, StoredBranding } from '../../api/brandingApi';
import { logoSrc } from '../../api/brandingApi';
import { ui } from '../../design-system';

/** One editor for both scopes (SRS #38 workspace, #102 platform): stored values, the inherited value
 *  shown as placeholder, logo upload/remove, and a live preview of the header band and report footer. */
export interface BrandingFormProps {
  stored: StoredBranding;
  inherited: Brand;                 // what an empty field falls back to
  effective: Brand;                 // what renders now
  limits: Record<string, number>;
  readOnly?: boolean;
  showCreditToggle?: boolean;       // workspace only
  onSave: (patch: BrandingPatch) => Promise<void>;
  onUpload: (file: File) => Promise<void>;
  onRemoveLogo: () => Promise<void>;
  onReset?: () => Promise<void>;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

export const BrandingForm: React.FC<BrandingFormProps> = ({ stored, inherited, effective, limits, readOnly, showCreditToggle, onSave, onUpload, onRemoveLogo, onReset }) => {
  const [form, setForm] = useState<BrandingPatch>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setForm({ brand_name: stored.brand_name, tagline: stored.tagline, primary_color: stored.primary_color, accent_color: stored.accent_color,
      watermark_enabled: stored.watermark_enabled, watermark_text: stored.watermark_text, footer_text: stored.footer_text, platform_credit: stored.platform_credit });
  }, [stored]);

  const set = <K extends keyof BrandingPatch>(k: K, v: BrandingPatch[K]) => setForm((f) => ({ ...f, [k]: v }));
  const text = (k: 'brand_name' | 'tagline' | 'watermark_text' | 'footer_text', label: string) => (
    <label style={ui.label}>
      <span>{label} <span style={ui.muted}>({(form[k] ?? '').length}/{limits[k]})</span></span>
      <input style={ui.input} value={form[k] ?? ''} maxLength={limits[k]} placeholder={String((inherited as any)[k] ?? '')} disabled={readOnly}
        onChange={(e) => set(k, e.target.value || null)} aria-label={label} />
    </label>
  );
  const colour = (k: 'primary_color' | 'accent_color', label: string) => {
    const val = form[k] ?? '';
    const bad = val !== '' && !HEX.test(val);
    return (
      <label style={ui.label}>
        <span>{label}</span>
        <span style={ui.row(6)}>
          <input type="color" aria-label={`${label} picker`} value={HEX.test(val) ? val : (inherited as any)[k]} disabled={readOnly} onChange={(e) => set(k, e.target.value)} style={{ width: 36, height: 30, padding: 0, border: 'none', background: 'none' }} />
          <input style={{ ...ui.input, width: 110, ...(bad ? { borderColor: 'var(--ds-danger)' } : {}) }} value={val} placeholder={(inherited as any)[k]} maxLength={7} disabled={readOnly}
            onChange={(e) => set(k, e.target.value || null)} aria-label={label} aria-invalid={bad || undefined} />
          {val && !readOnly && <button type="button" style={ui.btnSm} onClick={() => set(k, null)}>inherit</button>}
        </span>
      </label>
    );
  };

  const previewPrimary = HEX.test(form.primary_color ?? '') ? (form.primary_color as string) : inherited.primary_color;
  const previewName = form.brand_name || inherited.brand_name;
  const previewTagline = form.tagline || inherited.tagline;
  const previewWatermark = form.watermark_enabled ?? inherited.watermark_enabled;
  const previewWatermarkText = form.watermark_text || inherited.watermark_text;
  const previewFooter = form.footer_text || inherited.footer_text || 'Confidential — not for redistribution';
  const invalid = ['primary_color', 'accent_color'].some((k) => { const v = (form as any)[k]; return v && !HEX.test(v); });

  const save = async () => {
    setBusy(true); setMsg(null);
    try { await onSave(form); setMsg({ tone: 'ok', text: 'Branding saved — reports and the app header use it from now on.' }); }
    catch (e) { setMsg({ tone: 'err', text: e instanceof Error ? e.message : 'Could not save' }); }
    finally { setBusy(false); }
  };
  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true); setMsg(null);
    try { await onUpload(f); setMsg({ tone: 'ok', text: `Logo uploaded: ${f.name}` }); }
    catch (err) { setMsg({ tone: 'err', text: err instanceof Error ? err.message : 'Upload failed' }); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  return (
    <div style={ui.grid(340, 16)}>
      <div style={ui.card}>
        <div style={ui.kicker}>Identity</div>
        {text('brand_name', 'Brand name')}
        {text('tagline', 'Tagline')}
        <div style={ui.row(14)}>{colour('primary_color', 'Primary colour')}{colour('accent_color', 'Accent colour')}</div>
        <div style={ui.kicker}>Logo</div>
        <div style={ui.row(10)}>
          {effective.logo_url && <img src={logoSrc(effective.logo_url) ?? undefined} alt="Current logo" style={{ height: 40, maxWidth: 160, objectFit: 'contain', background: 'var(--ds-surface-2)', borderRadius: 6, padding: 4 }} data-testid="logo-preview" />}
          {!readOnly && <>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={pick} aria-label="Upload logo" style={{ fontSize: '0.85rem' }} />
            {stored.logo_url && <button type="button" style={ui.btnSm} onClick={async () => { setBusy(true); try { await onRemoveLogo(); setMsg({ tone: 'ok', text: 'Logo removed' }); } finally { setBusy(false); } }} disabled={busy}>Remove logo</button>}
          </>}
        </div>
        <div style={ui.muted}>PNG, JPEG or WebP up to 1 MB. SVG is not accepted. Shown in the app header and on every report.</div>
        <div style={ui.kicker}>Reports</div>
        <label style={{ ...ui.label, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={previewWatermark} disabled={readOnly} onChange={(e) => set('watermark_enabled', e.target.checked)} aria-label="Watermark on reports" />
          <span>Watermark on reports</span>
          {form.watermark_enabled !== null && form.watermark_enabled !== undefined && !readOnly && <button type="button" style={ui.btnSm} onClick={() => set('watermark_enabled', null)}>inherit</button>}
        </label>
        {text('watermark_text', 'Watermark text')}
        {text('footer_text', 'Report footer line')}
        {showCreditToggle && (
          <label style={{ ...ui.label, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={form.platform_credit ?? true} disabled={readOnly} onChange={(e) => set('platform_credit', e.target.checked)} aria-label="Show Powered by Policyora" />
            <span>Show "Powered by Policyora" in the app and on reports</span>
          </label>
        )}
        {msg && <div style={ui.alert(msg.tone === 'ok' ? 'success' : 'danger')}>{msg.text}</div>}
        {!readOnly && (
          <div style={ui.row(8)}>
            <button type="button" style={ui.primary} onClick={save} disabled={busy || invalid}>Save branding</button>
            {onReset && <button type="button" style={ui.btn} onClick={async () => { if (window.confirm('Remove all custom branding and return to the platform brand?')) { await onReset(); setMsg({ tone: 'ok', text: 'Branding reset' }); } }} disabled={busy}>Reset to platform brand</button>}
          </div>
        )}
      </div>
      <div style={ui.card} data-testid="brand-preview">
        <div style={ui.kicker}>Preview</div>
        <div style={{ background: previewPrimary, color: 'var(--ds-text-on-dark)', padding: '10px 14px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
          {effective.logo_url && <img src={logoSrc(effective.logo_url) ?? undefined} alt="" style={{ height: 22 }} />}
          <b>{previewName}</b><span style={{ fontSize: '0.8rem', opacity: 0.85 }}>{previewTagline}</span>
          <span style={{ marginLeft: 'auto', fontSize: '0.75rem', opacity: 0.85 }}>Quarterly briefing · today</span>
        </div>
        <div style={{ position: 'relative', border: '1px solid var(--ds-border)', borderRadius: 8, padding: '18px 14px', minHeight: 140, overflow: 'hidden', background: 'var(--ds-surface)' }}>
          {previewWatermark && <div aria-hidden style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', transform: 'rotate(-30deg)', fontSize: 28, fontWeight: 800, color: 'var(--ds-text)', opacity: 0.07, whiteSpace: 'nowrap' }}>{previewWatermarkText} · Your organisation</div>}
          <div style={ui.muted}>Report body…</div>
        </div>
        <div style={{ ...ui.muted, fontSize: '0.75rem', borderTop: '1px solid var(--ds-border)', paddingTop: 6 }}>
          Generated for Your organisation · {previewName} report · {previewFooter}{showCreditToggle && (form.platform_credit ?? true) ? ' · Powered by Policyora' : ''}
        </div>
      </div>
    </div>
  );
};
